import React from "react";
import {
  Box, Button, ButtonBase, Chip,
  Divider, Drawer, IconButton, InputAdornment, Switch, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { ArrowDownward, ArrowUpward, Close, DragIndicator, Search } from "@mui/icons-material";
import { SortableList, SortableRow } from "../dnd/SortableList";
import toast from "react-hot-toast";
import UnsavedChangesPopup from "ui-components/popups/UnsavedChangesPopup";
import { T, PIPELINE_COLORS } from "utils/backrrTokens";
import { CATALOG, DENSITIES, PRESETS, WIDGET, move, reorder } from "../layout/catalog";

// Pill toggle: solid accent track when on, grey when off, white thumb inside (investor-web switch geometry).
const switchSx = {
  width: 38, height: 20, padding: 0, display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: "2px", color: "#fff",
    "&.Mui-checked": { transform: "translateX(18px)", color: "#fff", "& + .MuiSwitch-track": { backgroundColor: T.accent, opacity: 1 } },
    "&.Mui-focusVisible .MuiSwitch-thumb": { outline: `2px solid ${T.accent}`, outlineOffset: "2px" },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
    "&.Mui-disabled .MuiSwitch-thumb": { color: "#fff" },
  },
  "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 3px rgba(16,24,40,.2)" },
  "& .MuiSwitch-track": { borderRadius: "10px", backgroundColor: "#E4E7EC", opacity: 1, transition: "background-color .2s" },
};

const matches = (q, ...labels) => !q || labels.some((l) => String(l || "").toLowerCase().includes(q));

const ToggleRow = ({ id, label, meta, checked, disabled, indent, onChange }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pl: indent ? 2.5 : 0, py: indent ? 0.75 : 0.5, opacity: disabled ? 0.5 : 1 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography id={id} sx={{ fontSize: indent ? "0.82rem" : "0.9rem", fontWeight: indent ? 400 : 600, color: indent ? T.inkMuted : T.ink }}>{label}</Typography>
      {meta && <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle }}>{meta}</Typography>}
    </Box>
    <Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} inputProps={{ "aria-labelledby": id }} sx={switchSx} />
  </Box>
);

const MoveRow = ({ label, muted, indent, first, last, onUp, onDown, handleProps, dragging }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pl: indent ? 2.5 : 0, py: 0.75, borderRadius: "8px", background: dragging ? T.accentSoft : "transparent", boxShadow: dragging ? "0 6px 16px rgba(16,24,40,.12)" : "none" }}>
    {handleProps && (
      <Box {...handleProps} aria-label={`Drag ${label}`} sx={{ display: "grid", placeItems: "center", color: T.inkSubtle, cursor: "grab", mr: -0.5, "&:focus-visible": { outline: `2px solid ${T.accent}`, borderRadius: "4px" } }}>
        <DragIndicator sx={{ fontSize: 18 }} />
      </Box>
    )}
    <Typography sx={{ flex: 1, fontSize: indent ? "0.82rem" : "0.9rem", fontWeight: indent ? 400 : 500, color: muted ? T.inkSubtle : indent ? T.inkMuted : T.ink }}>
      {label}{muted ? " · hidden" : ""}
    </Typography>
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <IconButton size="small" aria-label={`Move ${label} up`} disabled={first} onClick={onUp} sx={{ border: `1px solid ${T.border}`, borderRadius: "8px" }}>
        <ArrowUpward sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" aria-label={`Move ${label} down`} disabled={last} onClick={onDown} sx={{ border: `1px solid ${T.border}`, borderRadius: "8px" }}>
        <ArrowDownward sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  </Box>
);

// Mini preview of a density: rows of blocks that get tighter left to right.
const DensityIcon = ({ cols }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: "3px", width: 40 }}>
    {Array.from({ length: cols * 2 }).map((_, i) => <Box key={i} sx={{ height: 10, borderRadius: "3px", background: "#D9CCFF" }} />)}
  </Box>
);

const WidgetsTab = ({ api, q }) => {
  const { values, setWidget, setItem } = api;
  return (
    <Box role="group" aria-label="Dashboard widgets">
      {CATALOG.filter((w) => matches(q, w.label, ...(w.items || []).map((i) => i.label))).map((w, i) => {
        const entry = values.widgets[w.key];
        const shown = w.items ? w.items.filter((it) => entry.items?.[it.key] !== false).length : null;
        return (
          <Box key={w.key}>
            {i > 0 && <Divider sx={{ my: 1.5, borderColor: T.border }} />}
            <ToggleRow
              id={`cz-${w.key}`}
              label={w.label}
              checked={entry.visible !== false}
              onChange={(v) => setWidget(w.key, v)}
              meta={w.items ? (entry.visible === false ? "Hidden" : `${shown} of ${w.items.length} shown`) : null}
            />
            {w.items && <Box sx={{ height: 4 }} />}
            {w.items?.filter((it) => matches(q, w.label, it.label)).map((it) => (
              <ToggleRow
                key={it.key}
                id={`cz-${w.key}-${it.key}`}
                indent
                label={it.label}
                checked={entry.items?.[it.key] !== false}
                disabled={entry.visible === false}
                onChange={(v) => setItem(w.key, it.key, v)}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

const LayoutTab = ({ api, q }) => {
  const { values, view, setOrder, setItemOrder, setDensity } = api;
  // Search narrows the list; dragging is off then (arrows still work), so indexes always match the full order.
  const shownOrder = values.order.filter((k) => matches(q, WIDGET[k]?.label, ...(WIDGET[k]?.items || []).map((i) => i.label)));
  return (
    <Box>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: T.ink }}>Dashboard density</Typography>
      <Typography sx={{ fontSize: "0.78rem", color: T.inkSubtle, mb: 1.5 }}>Choose how much space widgets use</Typography>
      <Box role="radiogroup" aria-label="Dashboard density" sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
        {DENSITIES.map((d, i) => {
          const on = values.density === d.key;
          return (
            <ButtonBase
              key={d.key}
              role="radio"
              aria-checked={on}
              onClick={() => setDensity(d.key)}
              sx={{ flexDirection: "column", gap: 1, p: 1.5, borderRadius: "12px", border: `1.5px solid ${on ? T.accent : T.border}`, background: on ? T.accentSoft : "transparent", transition: "border-color .15s, background .15s" }}
            >
              <DensityIcon cols={i + 1} />
              <Typography sx={{ fontSize: "0.8rem", color: on ? T.accent : T.inkMuted, fontWeight: on ? 600 : 400 }}>{d.label}</Typography>
            </ButtonBase>
          );
        })}
      </Box>

      <Divider sx={{ my: 2.5, borderColor: T.border }} />
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: T.ink }}>Arrange widgets</Typography>
      <Typography sx={{ fontSize: "0.78rem", color: T.inkSubtle, mb: 1 }}>Drag by the ⠿ handle, or use the arrows. Cards move within their widget.</Typography>
      {q && <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle, mb: 1 }}>Clear the search to drag; arrows still work.</Typography>}
      <SortableList ids={shownOrder} onReorder={(from, to) => setOrder(reorder(values.order, from, to))} disabled={Boolean(q)}>
        {shownOrder.map((k) => {
          const w = WIDGET[k];
          const idx = values.order.indexOf(k);
          const cards = values.itemOrder[k] || [];
          return (
            <SortableRow key={k} id={k}>
              {({ handleProps, isDragging }) => (
                <Box sx={{ borderBottom: `1px solid ${T.border}`, pb: 0.5, mb: 0.5, background: "#fff" }}>
                  <MoveRow
                    label={w.label}
                    muted={!view.widget(k)}
                    first={idx === 0}
                    last={idx === values.order.length - 1}
                    onUp={() => setOrder(move(values.order, k, -1))}
                    onDown={() => setOrder(move(values.order, k, 1))}
                    handleProps={q ? undefined : handleProps}
                    dragging={isDragging}
                  />
                  {cards.length > 0 && (
                    <SortableList ids={cards} onReorder={(from, to) => setItemOrder(k, reorder(cards, from, to))} disabled={Boolean(q)}>
                      {cards.map((c, ci) => (
                        <SortableRow key={c} id={c}>
                          {({ handleProps: cardHandle, isDragging: cardDragging }) => (
                            <MoveRow
                              indent
                              label={w.items.find((it) => it.key === c)?.label}
                              muted={!view.item(k, c)}
                              first={ci === 0}
                              last={ci === cards.length - 1}
                              onUp={() => setItemOrder(k, move(cards, c, -1))}
                              onDown={() => setItemOrder(k, move(cards, c, 1))}
                              handleProps={q ? undefined : cardHandle}
                              dragging={cardDragging}
                            />
                          )}
                        </SortableRow>
                      ))}
                    </SortableList>
                  )}
                </Box>
              )}
            </SortableRow>
          );
        })}
      </SortableList>
    </Box>
  );
};

const PresetsTab = ({ api }) => (
  <Box>
    <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: T.ink }}>Choose a dashboard starting point</Typography>
    <Typography sx={{ fontSize: "0.78rem", color: T.inkSubtle, mb: 1.5 }}>
      Presets rearrange widgets; what you hid stays hidden. Save to keep it.
    </Typography>
    <Box sx={{ display: "grid", gap: 1 }}>
      {PRESETS.map((p) => {
        const active = p.order.join() === api.values.order.join();
        return (
          <ButtonBase
            key={p.key}
            onClick={() => api.applyPreset(p)}
            aria-pressed={active}
            sx={{ justifyContent: "space-between", textAlign: "left", p: 2, borderRadius: "12px", border: `1.5px solid ${active ? T.accent : T.border}`, background: active ? T.accentSoft : "transparent" }}
          >
            <Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: T.ink }}>{p.label}</Typography>
              <Typography sx={{ fontSize: "0.78rem", color: T.inkSubtle }}>{p.description}</Typography>
            </Box>
            <Typography aria-hidden sx={{ color: active ? T.accent : T.inkSubtle }}>{active ? "✓" : "→"}</Typography>
          </ButtonBase>
        );
      })}
    </Box>
  </Box>
);

const TABS = ["widgets", "layout", "presets"];

// Right-side customize drawer. Any change puts it in edit mode (Formik `dirty`); leaving the tab
// or the drawer then asks to save or discard first, so a draft is never lost or half-applied.
const CustomizeDrawer = ({ open, onClose, onReopen, api }) => {
  const [tab, setTab] = React.useState("widgets");
  const [q, setQ] = React.useState("");
  const [pending, setPending] = React.useState(null); // { type: 'tab', value } | { type: 'close' }

  const run = (action) => {
    if (action.type === "tab") setTab(action.value);
    if (action.type === "close") { setTab("widgets"); onClose(); }
  };
  const guard = (action) => (api.dirty ? setPending(action) : run(action));

  const discardAndGo = () => { api.discard(); run(pending); setPending(null); };
  // Saves are optimistic: move on at once; if the save fails, the draft is back and the drawer reopens.
  const saveAndClose = () => {
    setTab("widgets");
    onClose();
    api.save().then((ok) => { if (!ok) onReopen?.(); });
  };
  const cancel = () => { api.discard(); setTab("widgets"); onClose(); };
  const resetDefault = async () => {
    const undo = await api.resetAndSave();
    if (!undo) return;
    toast((t) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography sx={{ fontSize: "0.875rem", color: T.ink }}>Layout reset to default</Typography>
        <Button size="small" onClick={() => { toast.dismiss(t.id); undo(); }} sx={{ color: T.accent, fontWeight: 600, textTransform: "none", minWidth: 0 }}>Undo</Button>
      </Box>
    ), { id: "dashboard-layout-reset", duration: 6000, position: "bottom-center" });
  };

  const query = q.trim().toLowerCase();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => guard({ type: "close" })}
        PaperProps={{ sx: { width: { xs: "100vw", sm: 420 }, display: "flex", flexDirection: "column", borderRadius: { sm: "16px 0 0 16px" } } }}
      >
        <Box sx={{ p: 3, pb: 2, display: "flex", justifyContent: "space-between", gap: 2, borderBottom: `1px solid ${T.border}` }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="h2" sx={{ fontSize: "1.25rem", fontWeight: 700, color: T.ink }}>Customize dashboard</Typography>
              {api.dirty && <Chip size="small" label="Editing" sx={{ height: 22, fontSize: "0.7rem", background: T.accentSoft, color: T.accent, fontWeight: 600 }} />}
            </Box>
            <Typography sx={{ fontSize: "0.82rem", color: T.inkSubtle }}>Show, hide, arrange and space out your widgets.</Typography>
          </Box>
          <IconButton aria-label="Close customize" onClick={() => guard({ type: "close" })} sx={{ border: `1px solid ${T.border}`, borderRadius: "10px", alignSelf: "flex-start" }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2 }}>
          {tab !== "presets" && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search widgets..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              inputProps={{ "aria-label": "Search widgets" }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: T.inkSubtle }} /></InputAdornment> }}
              sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          )}
          <Tabs
            value={tab}
            onChange={(_, v) => v !== tab && guard({ type: "tab", value: v })}
            aria-label="Customize sections"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", borderRadius: "10px", fontWeight: 500, color: T.inkMuted }, "& .Mui-selected": { color: `${T.accent} !important`, background: T.accentSoft, fontWeight: 600 } }}
          >
            {TABS.map((t) => <Tab key={t} value={t} label={t[0].toUpperCase() + t.slice(1)} />)}
          </Tabs>
        </Box>
        <Divider sx={{ mt: 1.5, borderColor: T.border }} />

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
          {tab === "widgets" && <WidgetsTab api={api} q={query} />}
          {tab === "layout" && <LayoutTab api={api} q={query} />}
          {tab === "presets" && <PresetsTab api={api} />}
        </Box>

        <Box sx={{ p: 2.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
          <Typography role="status" aria-live="polite" sx={{ minHeight: 18, mb: 1, textAlign: "right", fontSize: "0.74rem", color: api.saveFailed ? T.down : T.accent }}>
            {api.saveFailed ? "Could not save. Try again." : api.dirty ? "Unsaved changes" : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, "& .MuiButton-root": { whiteSpace: "nowrap", textTransform: "none", borderRadius: "10px" } }}>
            <Button variant="outlined" onClick={resetDefault} sx={{ color: T.ink, borderColor: T.border }}>Reset default</Button>
            <Button variant="outlined" onClick={cancel} sx={{ color: T.ink, borderColor: T.border }}>Cancel</Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              disabled={!api.dirty || api.isSaving}
              onClick={saveAndClose}
              sx={{
                minWidth: 116,
                background: T.accent,
                color: "#fff",
                boxShadow: "none",
                "&:hover": { background: PIPELINE_COLORS.invested, boxShadow: "none" },
                "&.Mui-disabled": { background: T.grid, color: T.inkFaint },
              }}
            >
              Save layout
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Backrr's shared unsaved-changes popup, so this matches every other form. */}
      <UnsavedChangesPopup
        open={Boolean(pending)}
        handleClose={() => setPending(null)}
        handleSubmission={discardAndGo}
        // Same 16px corners as the drawer it guards.
        sx={{ "& .MuiDialog-paper": { width: "100%", maxWidth: "500px", borderRadius: "16px", p: 1 }, "& .MuiDialogActions-root button": { borderRadius: "12px" } }}
      />
    </>
  );
};

export default CustomizeDrawer;
